import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function Profile() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [dob, setDob] = useState(user?.dob || "");
  const [sex, setSex] = useState(user?.sex || "");
  const [heightCm, setHeightCm] = useState(user?.height_cm ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/profile", {
        name: name || null,
        dob: dob || null,
        sex: sex || null,
        height_cm: heightCm === "" ? null : Number(heightCm),
      });
      const nextUser = data?.user || { ...user, name, dob, sex, height_cm: heightCm };
      setUser(nextUser);
      localStorage.setItem("mh_user", JSON.stringify(nextUser));
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const doLogout = () => { logout(); navigate("/login", { replace: true }); };

  return (
    <div className="space-y-6 max-w-xl" data-testid="profile-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Profile</h1>
        <p className="text-neutral-600 mt-1 text-sm">{user?.email}</p>
      </div>
      <div className="bg-white rounded-2xl border border-neutral-200 p-5 space-y-4">
        <div>
          <label className="text-sm text-neutral-700 mb-1 block">Name</label>
          <Input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm text-neutral-700 mb-1 block">Date of birth</label>
            <Input data-testid="profile-dob" type="date" value={dob || ""} onChange={(e) => setDob(e.target.value)} />
          </div>
          <div>
            <label className="text-sm text-neutral-700 mb-1 block">Sex</label>
            <select
              data-testid="profile-sex"
              value={sex || ""}
              onChange={(e) => setSex(e.target.value)}
              className="w-full h-9 rounded-md border border-neutral-200 bg-white px-3 text-sm"
            >
              <option value="">—</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
              <option value="other">Other</option>
              <option value="undisclosed">Prefer not to say</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-sm text-neutral-700 mb-1 block">Height (cm)</label>
          <Input data-testid="profile-height" type="number" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} />
        </div>
        <div className="flex justify-end">
          <Button data-testid="profile-save" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
      <div>
        <Button variant="outline" onClick={doLogout} data-testid="profile-logout">Log out</Button>
      </div>
    </div>
  );
}
